import React, { useState, useEffect, useRef } from 'react';
import { Upload, RotateCcw, Check, Loader2, Image as ImageIcon } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { invalidateDefaultAvatarCache } from '../../services/defaultAvatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DefaultAvatarTab = () => {
  const [setting, setSetting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/default-avatar`, { withCredentials: true });
      setSetting(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const currentUrl = setting?.url
    ? (setting.url.startsWith('http') ? setting.url : `${BACKEND_URL}${setting.url}`)
    : null;
  const previousUrl = setting?.previous_url
    ? (setting.previous_url.startsWith('http') ? setting.previous_url : `${BACKEND_URL}${setting.previous_url}`)
    : null;

  const handleFilePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      setError('Please choose a JPEG, PNG, WebP, or GIF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be smaller than 10MB');
      return;
    }
    setError('');
    setPreviewFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const cancelPreview = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setPreviewFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const saveNew = async () => {
    if (!previewFile) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const fd = new FormData();
      fd.append('file', previewFile);
      const res = await axios.post(`${API}/admin/default-avatar`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess('Default avatar updated for all profiles without a photo.');
      invalidateDefaultAvatarCache();
      cancelPreview();
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const restorePrevious = async () => {
    if (!window.confirm('Restore the previous default avatar?')) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(`${API}/admin/default-avatar/restore`, {}, { withCredentials: true });
      invalidateDefaultAvatarCache();
      setSuccess('Previous default avatar restored.');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to restore');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#009E60] border-t-transparent mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFilePick}
        className="hidden"
        data-testid="default-avatar-file"
      />

      <p className="text-sm text-gray-500">
        The default profile image is used whenever a member has not uploaded their own photo. Uploading a new default replaces it site-wide.
      </p>

      {error && <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm" data-testid="avatar-error">{error}</div>}
      {success && <div className="p-3 bg-green-50 text-green-700 rounded-xl text-sm" data-testid="avatar-success">{success}</div>}

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Current */}
        <div className="bg-white border border-gray-100 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <ImageIcon className="w-4 h-4 text-[#009E60]" />
            <h3 className="font-semibold text-gray-900">Current Default</h3>
          </div>
          {currentUrl ? (
            <img src={currentUrl} alt="Current default" className="w-full aspect-square object-cover rounded-xl" data-testid="current-avatar-img" />
          ) : (
            <div className="w-full aspect-square rounded-xl bg-gray-100 flex items-center justify-center text-gray-400">No image</div>
          )}
          <div className="text-xs text-gray-400 mt-2">
            Updated: {setting?.updated_at ? new Date(setting.updated_at).toLocaleString() : '—'}
          </div>
        </div>

        {/* Preview / Upload */}
        <div className="bg-white border-2 border-dashed border-gray-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Upload className="w-4 h-4 text-[#009E60]" />
            <h3 className="font-semibold text-gray-900">{preview ? 'Preview' : 'Upload New'}</h3>
          </div>
          {preview ? (
            <>
              <img src={preview} alt="Preview" className="w-full aspect-square object-cover rounded-xl" data-testid="avatar-preview-img" />
              <div className="flex gap-2 mt-3">
                <Button onClick={cancelPreview} variant="outline" className="flex-1 rounded-xl" disabled={saving}>
                  Cancel
                </Button>
                <Button
                  onClick={saveNew}
                  disabled={saving}
                  className="flex-1 bg-[#009E60] hover:bg-[#007A4B] rounded-xl"
                  data-testid="save-default-avatar"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> Save</>}
                </Button>
              </div>
            </>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full aspect-square rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center hover:border-[#009E60] hover:bg-[#009E60]/5 transition-colors"
              data-testid="pick-default-avatar"
            >
              <Upload className="w-8 h-8 text-gray-400 mb-2" />
              <span className="text-sm text-gray-500">Choose image</span>
              <span className="text-xs text-gray-400 mt-1">JPEG, PNG, WebP, GIF up to 10MB</span>
            </button>
          )}
        </div>
      </div>

      {/* Restore previous */}
      {previousUrl && previousUrl !== currentUrl && (
        <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center gap-4">
          <img src={previousUrl} alt="Previous default" className="w-16 h-16 object-cover rounded-xl" data-testid="previous-avatar-img" />
          <div className="flex-1">
            <div className="font-semibold text-gray-900 text-sm">Previous default</div>
            <div className="text-xs text-gray-500">Restore this image if you changed your mind.</div>
          </div>
          <Button
            onClick={restorePrevious}
            disabled={saving}
            variant="outline"
            className="rounded-xl"
            data-testid="restore-default-avatar"
          >
            <RotateCcw className="w-4 h-4 mr-1" /> Restore
          </Button>
        </div>
      )}
    </div>
  );
};

export default DefaultAvatarTab;
